/**
 * Task Edit Modal
 * Trello-style modal for editing task cards with labels and due dates
 * Labels are consistent - each color has one name across all cards
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { DatePickerDropdown } from './DatePickerDropdown';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useClock24Hour } from '@/common/hooks/useClock24Hour';
import { useSession } from '@/features/auth/hooks/useSession';
import { LABEL_COLORS, DEFAULT_LABEL_COLOR } from '../shared/colors';
import { localDateTimeToISO, dueAtToLocalDate, dueAtToLocalTime } from './utils/date';
import { TaskTimePicker } from './components/TaskTimePicker';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { UnsavedChangesGuardModal } from '@/common/components/UnsavedChangesGuardModal';
import { StatusDropdown } from './components/StatusDropdown';
import { LabelDropdown } from './components/LabelDropdown';
import { TaskViewOnlyBody } from './components/TaskViewOnlyBody';
import { NotesList } from './components/NotesList';
import type { TaskNote, AssignedUser, TaskObject, TaskEditModalProps } from './taskEditModal.types';
import './TaskEditModal.css';

export function TaskEditModal({
  task,
  allTasks,
  conversationMembers = [],
  module,
  columns = [],
  onUpdateModuleLabels,
  isNewCard = false,
  viewOnly = false,
  // columnColor prop intentionally unused — the editor stays uniform gray.
  onClose,
  onSave,
  onDelete,
  onArchive
}: TaskEditModalProps) {
  // Track if we're editing a note (used by escape key handler)
  const isEditingNoteRef = useRef(false);

  // Unsaved-changes guard (escape-layer contract, issue #3938). isDirtyRef is set
  // by markDirty (wired to the form's onInput/onChange below); handleSafeClose
  // routes Esc / X / backdrop through the confirmation when dirty instead of
  // discarding silently. useEscapeKey already preventDefaults, so the module-pane
  // Esc handler declines and the Tasks pane survives this keypress.
  const isDirtyRef = useRef(false);
  const markDirty = () => { isDirtyRef.current = true; };
  const [showUnsavedConfirmation, setShowUnsavedConfirmation] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSafeClose = () => {
    if (isDirtyRef.current) {
      setShowUnsavedConfirmation(true);
      return;
    }
    onClose();
  };

  // Close on escape key (but not when editing a note)
  useEscapeKey(() => {
    if (isEditingNoteRef.current) {
      // Don't close modal, let the note editing handle escape
      return;
    }
    handleSafeClose();
  });

  // Handle overlay clicks with buffer zone to prevent accidental closures during copy/paste
  // Also prevent closing when editing a note
  const [taskModalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(() => {
    if (!isEditingNoteRef.current) {
      handleSafeClose();
    }
  }, 30);

  const { profile } = useSession();

  // Hide mobile header/footer when modal is open via body class
  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const [title, setTitle] = useState(task.title || '');
  const [dueAt, setDueAt] = useState(dueAtToLocalDate(task.dueAt));
  const [dueTime, setDueTime] = useState(dueAtToLocalTime(task.dueAt));
  const [use24h] = useClock24Hour();
  const [notes, setNotes] = useState<TaskNote[]>(Array.isArray(task.notes) ? task.notes : []);
  const [owner, setOwner] = useState<AssignedUser | null>(null);
  const [label, setLabel] = useState(task.label || '');
  const [labelColor, setLabelColor] = useState(task.labelColor || DEFAULT_LABEL_COLOR);
  const [customLabelInput, setCustomLabelInput] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLOR); // Separate color for "Create New" section
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [labelDropdownPosition, setLabelDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const labelButtonRef = useRef<HTMLButtonElement>(null);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [showDeleteButton, setShowDeleteButton] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  // View-only mode editing states
  const [showOwnerPicker, setShowOwnerPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ taskId: string; title: string } | null>(null);
  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Get current column label from status
  const currentColumn = columns.find(col => col.status === task.status);
  const currentColumnLabel = currentColumn?.label || task.status || 'To Do';

  // Close status dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    }
    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStatusDropdown]);

  // Handle status change
  const handleStatusChange = (newStatus: string) => {
    onSave({ status: newStatus as TaskObject['status'] });
    setShowStatusDropdown(false);
  };

  // Get labels from module config (not from tasks)
  const moduleLabels = (module?.config?.labels || []) as Array<{ name: string; color: string }>;

  // Existing labels come from module config only
  const existingLabels = moduleLabels;

  const labelColors = LABEL_COLORS;

  // Initialize form state from task props
  // Use task.id as dependency to avoid infinite loops when task object reference changes
  useEffect(() => {
    setTitle(task.title || '');
    setDueAt(dueAtToLocalDate(task.dueAt));
    setDueTime(dueAtToLocalTime(task.dueAt));
    setLabel(task.label || '');
    setLabelColor(task.labelColor || DEFAULT_LABEL_COLOR);
    setCustomLabelInput('');
    setNewLabelColor(DEFAULT_LABEL_COLOR);
    setNewNote('');
    setEditingNoteId(null);
    setEditingNoteText('');

    // Get the single owner (first assigned user)
    const firstAssigned = task.assignedTo?.[0];
    if (firstAssigned) {
      if (typeof firstAssigned === 'string') {
        // Old format: look up name from conversationMembers
        const member = conversationMembers.find((m: any) => m.id === firstAssigned);
        if (member) {
          setOwner({
            id: firstAssigned,
            name: member.name || member.email,
          });
        } else {
          // Member not found yet - set with ID only, will show in dropdown when conversationMembers loads
          setOwner({
            id: firstAssigned,
            name: 'Loading...',
          });
        }
      } else {
        // New format: prefer fresh conversationMembers name, fall back to embedded snapshot
        const member = conversationMembers.find((m: any) => m.id === firstAssigned.id);
        setOwner({
          id: firstAssigned.id,
          name: member?.name || member?.email || firstAssigned.name || 'Unknown',
        });
      }
    } else {
      setOwner(null);
    }

    // Migrate legacy notes that may have old format (date/note) or missing author info
    const migratedNotes = (Array.isArray(task.notes) ? task.notes : []).map((note: any) => ({
      id: note.id,
      // Handle old format: 'note' field -> 'content' field
      content: note.content || note.note || '',
      authorId: note.authorId || profile?.id || '',
      authorName: note.authorName || profile?.name || 'Unknown',
      // Handle old format: 'date' field -> 'createdAt' field
      createdAt: note.createdAt || note.date || new Date().toISOString(),
      updatedAt: note.updatedAt,
    }));
    setNotes(migratedNotes);
  }, [task.id, task.title, task.dueAt, task.label, task.labelColor, task.assignedTo, task.notes, profile?.id, profile?.name, conversationMembers]);

  // Update label dropdown position when opened
  useEffect(() => {
    if (showLabelDropdown && labelButtonRef.current) {
      const rect = labelButtonRef.current.getBoundingClientRect();
      setLabelDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showLabelDropdown]);

  // Close label dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) {
        setShowLabelDropdown(false);
        // Clear hover states when dropdown closes
        setHoveredLabel(null);
        setShowDeleteButton(null);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup timeout on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    // For new cards, pass all the data directly
    if (isNewCard) {
      onSave({
        title: trimmedTitle,
        dueAt: dueAt ? localDateTimeToISO(dueAt, dueTime || undefined) : undefined,
        notes: notes.length > 0 ? notes : undefined,
        assignedTo: owner ? [owner] : undefined,
        label: label.trim() || undefined,
        labelColor: label.trim() ? labelColor : undefined,
      });
      return;
    }

    // For existing cards, compute the patch
    const patch: any = {};

    if (trimmedTitle !== task.title) patch.title = trimmedTitle;
    const origDueDate = dueAtToLocalDate(task.dueAt);
    const origDueTime = dueAtToLocalTime(task.dueAt);
    if (dueAt !== origDueDate || dueTime !== origDueTime) {
      patch.dueAt = dueAt ? localDateTimeToISO(dueAt, dueTime || undefined) : null;
    }

    // Check if label changed
    if (label.trim() !== (task.label || '')) {
      patch.label = label.trim() || '';
      patch.labelColor = label.trim() ? labelColor : '';
    } else if (labelColor !== task.labelColor && label.trim()) {
      patch.labelColor = labelColor;
    }

    // Check if notes changed
    const originalNotes = task.notes || [];
    if (JSON.stringify(notes) !== JSON.stringify(originalNotes)) {
      patch.notes = notes.length > 0 ? notes : [];
    }

    // Check if owner changed
    const originalOwner = task.assignedTo?.[0];
    const ownerChanged = owner?.id !== (typeof originalOwner === 'string' ? originalOwner : originalOwner?.id);
    if (ownerChanged) {
      patch.assignedTo = owner ? [owner] : [];
    }

    if (Object.keys(patch).length > 0) {
      onSave(patch);
    } else {
      onClose();
    }
  };

  // Global Enter key shortcut to save when not focused on input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input, textarea, or select
      const activeElement = document.activeElement;
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT'
      );

      // Enter saves when not typing in an input
      if (e.key === 'Enter' && !isTyping) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit]);

  // Save individual field changes from view-only mode
  const handleViewOnlySave = (field: 'owner' | 'dueAt', value: AssignedUser | null | string) => {
    const patch: any = {};
    if (field === 'owner') {
      const owner = value as AssignedUser | null;
      patch.assignedTo = owner ? [owner] : [];
      setOwner(owner);
    } else if (field === 'dueAt') {
      patch.dueAt = value ? localDateTimeToISO(value as string, dueAtToLocalTime(task.dueAt) || undefined) : null;
      setDueAt(value as string);
    }
    onSave(patch);
  };

  // Note management functions
  const handleAddNote = () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;

    const newNoteObj: TaskNote = {
      id: Date.now().toString(),
      content: trimmed,
      authorId: profile?.id || '',
      authorName: profile?.name || 'Unknown',
      createdAt: new Date().toISOString(),
    };

    setNotes([newNoteObj, ...notes]);
    setNewNote('');
  };

  const handleDeleteNote = (id: string) => {
    const updatedNotes = notes.filter(n => n.id !== id);
    setNotes(updatedNotes);
  };

  const handleEditNote = (noteObj: TaskNote) => {
    setEditingNoteId(noteObj.id);
    setEditingNoteText(noteObj.content);
    isEditingNoteRef.current = true;
  };

  const handleSaveNoteEdit = () => {
    if (!editingNoteId) return;
    const trimmed = editingNoteText.trim();
    if (!trimmed) {
      // If empty, delete the note
      const updatedNotes = notes.filter(n => n.id !== editingNoteId);
      setNotes(updatedNotes);
    } else {
      // Just update local state, don't save to backend yet
      const updatedNotes = notes.map(n =>
        n.id === editingNoteId ? { ...n, content: trimmed, updatedAt: new Date().toISOString() } : n
      );
      setNotes(updatedNotes);
    }
    setEditingNoteId(null);
    setEditingNoteText('');
    // Delay resetting the ref to allow overlay click handler to check it first
    setTimeout(() => {
      isEditingNoteRef.current = false;
    }, 100);
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
    // Delay resetting the ref to allow overlay click handler to check it first
    setTimeout(() => {
      isEditingNoteRef.current = false;
    }, 100);
  };

  // Select an existing module label
  const handleSelectExistingLabel = (existingLabel: { name: string; color: string }) => {
    setLabel(existingLabel.name);
    setLabelColor(existingLabel.color);
    setCustomLabelInput('');
    setShowLabelDropdown(false);
  };

  // Create (or select) a label with the given name/color.
  // Collapses the previously triplicated Enter / + button / swatch handlers;
  // preserves the exact ordering: add-to-module (try/catch) → select → clear → close.
  const handleCreateLabel = async (name: string, color: string) => {
    const newLabel = { name, color };
    // Add to module labels if not already exists
    if (!existingLabels.some(l => l.name === newLabel.name)) {
      const updatedLabels = [...existingLabels, newLabel];
      // Save to module config immediately
      if (onUpdateModuleLabels) {
        try {
          await onUpdateModuleLabels(updatedLabels);
        } catch (err) {
          console.error('Failed to save label to module:', err);
        }
      }
    }
    // Select the new label and close dropdown (don't save task yet)
    setLabel(newLabel.name);
    setLabelColor(newLabel.color);
    setCustomLabelInput('');
    setShowLabelDropdown(false);
  };

  // Delete a label from module config (and clear it if currently selected)
  const handleDeleteLabel = async (existingLabel: { name: string; color: string }) => {
    // Remove from module labels
    const updatedLabels = existingLabels.filter(l => l.name !== existingLabel.name);
    if (onUpdateModuleLabels) {
      try {
        await onUpdateModuleLabels(updatedLabels);
      } catch (err) {
        console.error('Failed to delete label from module:', err);
      }
    }
    // Clear if this is the currently selected label
    if (label === existingLabel.name) {
      setLabel('');
      setLabelColor(DEFAULT_LABEL_COLOR);
    }
  };

  // Render delete confirmation modal
  const renderDeleteConfirmationModal = () => {
    if (!deleteConfirmation) return null;

    return (
      <DeleteConfirmationModal
        onCancel={() => setDeleteConfirmation(null)}
        onConfirm={async () => {
          if (onDelete) {
            onDelete(deleteConfirmation.taskId);
          }
          setDeleteConfirmation(null);
          onClose();
        }}
      />
    );
  };

  // Use portal to render modal at document root, escaping any parent stacking contexts
  const modalContent = (
    <div
      className="task-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        pointerEvents: 'auto',
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={taskModalContentRef}
        className="task-modal-content"
        style={{
          // One uniform light-gray card — white inputs on top; no inset panels,
          // no column color in the editor (tried and reverted 2026-06-10).
          background: '#f4f5f6',
          borderRadius: 16,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: 512,
          width: '100%',
          maxHeight: 'calc(100vh - 32px)',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 17, fontWeight: 500, color: '#191d22', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {viewOnly ? (title || 'Untitled Task') : isNewCard ? 'New Task' : 'Edit Task'}
            </h2>
            {/* Status token dropdown - only show if columns available and not new card */}
            {columns.length > 0 && !isNewCard && (
              <StatusDropdown
                columns={columns}
                currentColumn={currentColumn}
                currentColumnLabel={currentColumnLabel}
                taskStatus={task.status}
                show={showStatusDropdown}
                onToggle={() => setShowStatusDropdown(!showStatusDropdown)}
                onSelect={handleStatusChange}
                dropdownRef={statusDropdownRef}
              />
            )}
          </div>
          <button
              onClick={handleSafeClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                background: '#eef0f2',
                border: 'none',
                borderRadius: '50%',
                color: '#6e7680',
                fontSize: 28,
                lineHeight: 1,
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e6e8ea'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#eef0f2'; }}
            >
              ×
            </button>
        </div>

        {/* View Only Mode - Clean display with labels */}
        {viewOnly ? (
          <TaskViewOnlyBody
            task={task}
            owner={owner}
            dueAt={dueAt}
            notes={notes}
            conversationMembers={conversationMembers}
            showOwnerPicker={showOwnerPicker}
            showDatePicker={showDatePicker}
            setShowOwnerPicker={setShowOwnerPicker}
            setShowDatePicker={setShowDatePicker}
            onViewOnlySave={handleViewOnlySave}
          />
        ) : (
        <form ref={formRef} onSubmit={handleSubmit} onInput={markDirty} onChange={markDirty} className="overflow-y-auto flex-1">
          {/* Form body — no inset card: the whole modal is one uniform gray
              surface (per mock); the white inputs provide the contrast. The
              column color deliberately never appears in the editor. */}
          <div style={{
            margin: '10px 16px 16px 16px',
            padding: '0 0 4px',
          }}>
            {/* Task */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
              <textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Card content"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                style={{ background: '#ffffff', outline: 'none' }}
                rows={4}
                autoFocus
              />
            </div>

            {/* Label */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <div className="flex gap-2 items-center">
                {/* Dropdown trigger with selected label or placeholder */}
                <div className="flex-1 relative">
                  <button
                    ref={labelButtonRef}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowLabelDropdown(!showLabelDropdown);
                    }}
                    className="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm text-left flex items-center gap-2"
                    style={{ background: '#ffffff', outline: 'none' }}
                  >
                    {label ? (
                      <span
                        className="px-2.5 py-0.5 rounded-md text-xs font-medium"
                        style={{ backgroundColor: labelColor, color: '#23282e' }}
                      >
                        {label}
                      </span>
                    ) : (
                      <span className="text-gray-400">Add a label</span>
                    )}
                  </button>
                </div>

                {/* Clear button - always present as placeholder */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLabel('');
                    setCustomLabelInput('');
                    setLabelColor(DEFAULT_LABEL_COLOR);
                  }}
                  className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
                  style={{ visibility: label.trim() ? 'visible' : 'hidden' }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Dropdown menu - portal style with fixed positioning */}
            {showLabelDropdown && (
              <LabelDropdown
                dropdownRef={labelDropdownRef}
                position={labelDropdownPosition}
                existingLabels={existingLabels}
                labelColors={labelColors}
                customLabelInput={customLabelInput}
                newLabelColor={newLabelColor}
                hoveredLabel={hoveredLabel}
                showDeleteButton={showDeleteButton}
                hoverTimeoutRef={hoverTimeoutRef}
                setCustomLabelInput={setCustomLabelInput}
                setNewLabelColor={setNewLabelColor}
                setHoveredLabel={setHoveredLabel}
                setShowDeleteButton={setShowDeleteButton}
                onSelectExisting={handleSelectExistingLabel}
                onCreateLabel={handleCreateLabel}
                onDeleteLabel={handleDeleteLabel}
              />
            )}

            {/* Assignee - Single assignee dropdown */}
            {conversationMembers.length >= 1 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <div className="flex gap-2 items-center">
                  <select
                    value={owner?.id || ''}
                    onChange={(e) => {
                      const memberId = e.target.value;
                      if (!memberId) {
                        setOwner(null);
                        return;
                      }
                      const member = conversationMembers.find((m: any) => m.id === memberId);
                      if (member) {
                        setOwner({ id: memberId, name: member.name || member.email });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none"
                    style={{
                      background: '#ffffff',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem',
                      outline: 'none'
                    }}
                  >
                    <option value="">Unassigned</option>
                    {conversationMembers.map((member: any) => (
                      <option key={member.id} value={member.id}>
                        {member.name || member.email}
                      </option>
                    ))}
                  </select>

                  {/* Clear button - always present as placeholder */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOwner(null);
                    }}
                    className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
                    style={{ visibility: owner ? 'visible' : 'hidden' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Due Date (with optional time-of-day) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <div className="task-due-row">
                <DatePickerDropdown
                  value={dueAt}
                  onChange={setDueAt}
                  onClear={() => { setDueAt(''); setDueTime(''); }}
                />
                <TaskTimePicker
                  value={dueTime}
                  onChange={setDueTime}
                  use24h={use24h}
                  disabled={!dueAt}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>

              {/* Add new note */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  style={{ background: '#ffffff', outline: 'none' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddNote();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddNote();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl px-1"
                  style={{ visibility: newNote.trim() ? 'visible' : 'hidden' }}
                  title="Add note"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Notes list - outside identity block */}
          {notes.length > 0 && (
            <NotesList
              notes={notes}
              editingNoteId={editingNoteId}
              editingNoteText={editingNoteText}
              profileId={profile?.id}
              setEditingNoteText={setEditingNoteText}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              onSaveEdit={handleSaveNoteEdit}
              onCancelEdit={handleCancelNoteEdit}
            />
          )}

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 20px 20px 20px',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
            }}
          >
            {/* Archive button in bottom left - only show for existing tasks */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {!isNewCard && task?.id && onArchive && (
                <button
                  type="button"
                  onClick={() => {
                    onArchive(task.id);
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    color: '#9aa1a8',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-archive-bg)'; e.currentTarget.style.color = 'var(--color-action-archive-text)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9aa1a8'; }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 8v13H3V8"></path>
                    <path d="M1 3h22v5H1z"></path>
                    <path d="M10 12h4"></path>
                  </svg>
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: 'var(--color-action-cancel-bg)',
                border: '1px solid var(--color-action-cancel-border)',
                color: 'var(--color-action-cancel-text)',
                minWidth: '80px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--color-action-cancel-bg-hover)';
                e.currentTarget.style.borderColor = 'var(--color-action-cancel-border-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--color-action-cancel-bg)';
                e.currentTarget.style.borderColor = 'var(--color-action-cancel-border)';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                // Brand-blue Save on the cool panel (per mock 2026-06-10).
                background: 'var(--color-brand, #15a5e3)',
                border: 'none',
                color: '#ffffff',
                minWidth: '80px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--color-brand-hover, #1391c9)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--color-brand, #15a5e3)';
              }}
            >
              Save
            </button>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );

  // Render to dedicated modal portal root to escape any parent stacking contexts
  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) {
    console.error('Modal portal root not found');
    return null;
  }
  return (
    <Fragment>
      {createPortal(modalContent, portalRoot)}
      {deleteConfirmation && createPortal(renderDeleteConfirmationModal(), document.body)}
      {createPortal(
        <UnsavedChangesGuardModal
          show={showUnsavedConfirmation}
          onCancel={() => setShowUnsavedConfirmation(false)}
          onDiscard={onClose}
          // requestSubmit fires the form's onSubmit (handleSubmit), which saves
          // and then closes via onSave/onClose — same path as the Save button.
          onSave={() => formRef.current?.requestSubmit()}
        />,
        portalRoot,
      )}
    </Fragment>
  );
}
